import type { BriefAppendix, BriefCard, FollowUpRecord } from '@sigora/domain';
import { useId, useState } from 'react';

import {
  APPENDIX_HEADINGS,
  BRIEF_HEADINGS,
  FOLLOW_UP_LOG_EMPTY,
  appendixToggleLabel,
  followUpLogHeading,
  followUpLogLine,
  followUpOutcomeLine,
} from '../brief-copy.ts';
import { briefDate } from './brief-date.ts';
import { PollBar } from './PollBar.tsx';
import { type RevisionMarks, UpdatedTag } from './RevisionMarks.tsx';

/**
 * The Appendix, collapsed until asked for: the guide as it was bought, every poll's counts, and
 * every written answer by participant. It is the record behind the page rather than part of the
 * reading, and on a study of any size it is most of the page's length. An answer to a question the
 * stored guide does not name is listed under a heading of its own rather than a blank one. `marks`
 * the block a Sigora revision changed, beside its toggle.
 *
 * With the Brief's follow-up `record`, the Appendix ends on the follow-up log: every finalized
 * follow-up, oldest first, dated, with its question and how it landed, a note naming its card by
 * the research question among `cards`. Its toggle names how many there are. Without a record (a
 * past version) there is no log and the toggle names none.
 */
export const Appendix = ({
  appendix,
  marks,
  record,
  cards = [],
}: {
  appendix: BriefAppendix;
  marks?: RevisionMarks | null;
  record?: FollowUpRecord;
  /** The Brief's evidence cards, which name the card each note sits under. */
  cards?: readonly BriefCard[];
}) => {
  const [open, setOpen] = useState(false);
  const bodyId = useId();
  const questionText = new Map(
    appendix.guide.flatMap((section) =>
      section.questions.map((question) => [question.id, question.text] as const),
    ),
  );

  return (
    <>
      <h2>
        <button
          type="button"
          className="brief-appendix-toggle"
          data-test-id="brief-appendix-toggle"
          aria-expanded={open}
          aria-controls={bodyId}
          onClick={() => setOpen((shown) => !shown)}
        >
          {record === undefined
            ? BRIEF_HEADINGS.appendix
            : appendixToggleLabel(record.followUpCount)}
        </button>
        <UpdatedTag marks={marks} block="appendix" />
      </h2>

      {/* In the page while hidden, so the toggle's `aria-controls` always names an element. */}
      <div id={bodyId} data-test-id="brief-appendix-body" hidden={!open}>
        <h3>{APPENDIX_HEADINGS.guide}</h3>
        {appendix.guide.map((section, index) => (
          // Titles need not be unique, and the guide's order never changes in a stored version.
          <section key={String(index)} data-test-id="brief-appendix-section">
            <h4>{section.title}</h4>
            <ol className="brief-list">
              {section.questions.map((question) => (
                <li key={question.id} data-test-id="brief-appendix-question">
                  {question.text}
                  {question.options.length > 0 && (
                    <ul className="brief-list">
                      {question.options.map((option) => (
                        <li key={option.id}>{option.text}</li>
                      ))}
                    </ul>
                  )}
                </li>
              ))}
            </ol>
          </section>
        ))}

        {appendix.poll_tables.length > 0 && (
          <>
            <h3>{APPENDIX_HEADINGS.polls}</h3>
            {appendix.poll_tables.map((poll) => (
              <PollBar key={poll.dg_question_id} poll={poll} />
            ))}
          </>
        )}

        {appendix.answers.length > 0 && (
          <>
            <h3>{APPENDIX_HEADINGS.answers}</h3>
            {appendix.answers.map((answer) => (
              <section key={answer.dg_question_id} data-test-id="brief-appendix-answers">
                <h4>{questionText.get(answer.dg_question_id) ?? APPENDIX_HEADINGS.otherAnswers}</h4>
                <ul className="brief-list">
                  {answer.responses.map((response) => (
                    <li key={response.participant} data-test-id="brief-appendix-answer">
                      <span className="brief-meta">{response.participant}</span> {response.text}
                    </li>
                  ))}
                </ul>
              </section>
            ))}
          </>
        )}

        {record !== undefined && (
          <section data-test-id="brief-follow-up-log">
            <h3>{followUpLogHeading(record.followUpCount)}</h3>
            {record.log.length === 0 ? (
              <p data-test-id="brief-follow-up-log-empty">{FOLLOW_UP_LOG_EMPTY}</p>
            ) : (
              <ul className="brief-list">
                {record.log.map((entry) => (
                  <li
                    key={entry.followUpId}
                    data-test-id="brief-follow-up-log-entry"
                    data-kind={entry.outcome.kind}
                  >
                    {followUpLogLine(
                      briefDate(entry.finalizedAt),
                      entry.question,
                      followUpOutcomeLine(
                        entry.outcome,
                        entry.outcome.kind === 'note' ? cardText(cards, entry.outcome.rqId) : null,
                      ),
                    )}
                  </li>
                ))}
              </ul>
            )}
          </section>
        )}
      </div>
    </>
  );
};

const cardText = (cards: readonly BriefCard[], rqId: string): string | null =>
  cards.find((card) => card.rq_id === rqId)?.rq_text ?? null;
